/**
 * MFA Fragment - ES6 Module
 * Handles MFA recovery codes page interactions:
 * - Copy all codes to clipboard
 * - Enable/disable the continue button based on checkbox state
 */
import { Events } from '../../../js/utils/events.js';

let initialized = false;

const MfaHandlers = {
    /**
     * Copy all recovery codes to clipboard
     * Triggered by: <button data-mfa-copy-codes data-codes="...">
     */
    async onCopyCodes(e, element) {
        const codes = element.getAttribute('data-codes');
        if (!codes) return;

        try {
            await navigator.clipboard.writeText(codes);
            // Brief visual feedback
            const originalText = element.textContent;
            element.textContent = 'Copied!';
            setTimeout(() => {
                element.textContent = originalText;
            }, 2000);
        } catch (err) {
            // Fallback for non-secure contexts (http://localhost)
            const textarea = document.createElement('textarea');
            textarea.value = codes;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);

            const originalText = element.textContent;
            element.textContent = 'Copied!';
            setTimeout(() => {
                element.textContent = originalText;
            }, 2000);
        }
    },

    /**
     * Download recovery codes as a plain-text file
     * Triggered by: <button data-mfa-download-codes data-codes="...">
     */
    onDownloadCodes(e, element) {
        const codes = element.getAttribute('data-codes');
        if (!codes) return;

        const header = 'Leapfile Sign-In Verification — Recovery Codes\n'
            + 'Keep this file somewhere safe. Each code works one time only.\n\n';
        const blob = new Blob([header + codes + '\n'], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'leapfile-recovery-codes.txt';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Brief visual feedback, mirrors the copy-codes pattern
        const originalText = element.textContent;
        element.textContent = 'Downloaded!';
        setTimeout(() => {
            element.textContent = originalText;
        }, 2000);
    },

    /**
     * Toggle the continue/done button enabled state based on checkbox
     * Triggered by: <input data-mfa-codes-saved> checkbox
     */
    onCodesSavedToggle(e, element) {
        const btn = document.querySelector('[data-mfa-continue-btn]');
        if (btn) {
            btn.disabled = !element.checked;
        }
    }
};

/**
 * Initialize MFA fragment
 * Called from main.js on DOMContentLoaded
 */
export function initMfa() {
    if (initialized) return;
    initialized = true;

    Events.on('click', 'mfa-copy-codes', MfaHandlers.onCopyCodes);
    Events.on('click', 'mfa-download-codes', MfaHandlers.onDownloadCodes);
    Events.on('change', 'mfa-codes-saved', MfaHandlers.onCodesSavedToggle);

    // Re-wire after HTMX swaps (e.g., enrollment → recovery codes transition)
    document.addEventListener('htmx:afterSwap', () => {
        // Reset button state if a new recovery codes fragment was swapped in
        const checkbox = document.querySelector('[data-mfa-codes-saved]');
        const btn = document.querySelector('[data-mfa-continue-btn]');
        if (checkbox && btn) {
            btn.disabled = !checkbox.checked;
        }
    });
}
